
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AttachmentService } from '../../../../core/services/attachment.service';
import { ApiEndPoints, ApiRoot } from '../../../constants/api-endpoints';
import { SharedService } from '../../../services/shared.service';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { Utils } from '../../../../Utils/utils';
import { FolderResponse } from '../../../models/documents-response.model';
import { OptionFieldComponent } from "../option-field/option-field.component";
import { OptionTranslations } from '../../../models/option-translation.model';
import { OptionTranslateService } from '../../../services/option-translate.service';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SkeletonLoaderComponent } from "../../skeleton-loader/skeleton-loader.component";
import { EncryptionResponse } from '../../../models/encryption-response.model';
import { environment } from '../../../../../environments/environment';
import { NgClass } from '@angular/common';

type StampLocationRequirement = 'REQUIRED' | 'OPTIONAL' | 'NOT_APPLICABLE';
interface Attachment {
  name: string;
  documentId?: string;
  documentType?: string,
  url: string;
  progress: number;  //0-100 upload progress
  uploaded: boolean;
  serverResponse?: any;
  externalStampLoc: any,
  notes: string,
  canDelete: boolean,
  fileFormat?: string,
  fileSizeBytes?: number
}
@Component({
  selector: 'app-file',
  imports: [TranslateModule, ReactiveFormsModule, OptionFieldComponent, SkeletonLoaderComponent, NgClass],
  templateUrl: './file.component.html',
  styleUrl: './file.component.css'
})
export class FileComponent implements OnChanges, OnInit, OnDestroy {
  @Input() title: any = "";
  @Input() description: any = "";
  @Input() attachments: any[] = [];
  @Input() control: any;
  @Output() onUploadRemove = new EventEmitter<any>();
  @Output() removeFile = new EventEmitter<any>();
  @Output() folderMissing = new EventEmitter<void>();
  @Input() accept: any = "";
  @Input() chooseFromExisting: boolean = true;
  @Input() showAction: boolean = true;
  @Input() folder!: FolderResponse;
  @Input() documentType: string = "";
  @Input() notes: string = "";
  @Input() documentServiceType: string = "";
  @Input() allowedDocumentTypes: any[] = [];
  @Input() systemDocuments: Array<{ doc: any; folder?: FolderResponse }> = [];
  @Input() mot: string = "";
  @Input() svsSubject: string = "";
  @Input() showUploadInput: boolean = true;
  @Input() stampLocationRequirement: StampLocationRequirement = 'NOT_APPLICABLE';
  @Input() documentUploadUserType: string = "Customer"

  externalStampLoc: string = "";
  validation: string = "";
  stampPositions: any = [];
  lang: string = 'en';
  stampLocationControl: FormControl<string | null> = new FormControl<string | null>(null);
  private stampLocationSubscription?: Subscription;
  private suppressStampChange: boolean = false;  
  downloadingId: any | null = null;
  isRemoving: boolean = false;
  isUploading: boolean = false;
  uploadInputId: string = `upload-${Math.random().toString(36).slice(2, 11)}`;
  
  constructor(
    private cdr: ChangeDetectorRef,
    private attachmentService: AttachmentService,
    private sharedService: SharedService,
    private optionTranslateService: OptionTranslateService,
    private translateService: TranslateService,
  ) {
    this.lang = sharedService.getCurrentLang() ?? 'en';
    this.validation = Utils.getDubaiDateTime();
    this.stampPositions = this.optionTranslateService.getOptions(
      'stampPositions',
      this.lang
    );
  }

  ngOnInit(): void {
    this.stampLocationSubscription = this.stampLocationControl.valueChanges.subscribe((val) => {
      if (this.suppressStampChange) return;
      if (!this.stampLocationVisible) return;
      if (!this.attachments?.length) return;

      const existingStamp = this.getAttachmentStampCode(this.attachments[0]);
      const newStampCode = (val || '').toString().toUpperCase();

      if (existingStamp && existingStamp.toUpperCase() !== newStampCode && newStampCode) {
        this.removeAttachmentsForStampChange();
      }
    });
  }

  ngOnDestroy(): void {
    this.stampLocationSubscription?.unsubscribe();
  }

  // This method is used to handle the on changes lifecycle hook using changes
  ngOnChanges(changes: SimpleChanges): void {
    if (this.folder && (changes["folder"] || changes["documentType"] || changes["notes"])) {
      if (this.folder?.documents) {
        this.attachments = this.getDocumentsByType(this.folder?.documents, this.documentType);
        if (this.attachments) {
          if (this.attachments[0]?.externalStampLoc) {
            this.setStampLocationValue(this.attachments[0]?.externalStampLoc);
          }
          this.onUploadRemove.emit(this.attachments);
        }
      }
    }

    // When toggled on or language/options change, ensure default selection is Left
    if (changes["stampLocationRequirement"] || changes["folder"]) {
      if (this.stampLocationVisible) {
        // Add 'required' validator when needed
        const validators = this.isStampLocationRequired ? [Validators.required] : [];
        this.stampLocationControl.setValidators(validators);
      } else {
        // Remove validator if not needed
        this.stampLocationControl.clearValidators();
        this.setStampLocationValue(null);
      }
      this.stampLocationControl.updateValueAndValidity();
    }

    if (changes["showAction"]) {
      this.toggleStampLocationControl(this.showAction);
    }

  }

  private setStampLocationValue(value: string | null): void {
    this.suppressStampChange = true;
    this.stampLocationControl.setValue(value, { emitEvent: false });
    this.suppressStampChange = false;
  }

  private toggleStampLocationControl(enable: boolean): void {
    if (enable) {
      this.stampLocationControl.enable({ emitEvent: false });
    } else {
      this.stampLocationControl.disable({ emitEvent: false });
    }
  }

  validationError: any = "";
  // This method is used to handle file change event
  onFileChange(evt: Event): void {

    this.validationError = null;
    this.downloadingId = "";
    this.isDocumentsMissing = false;
    const input = evt.target as HTMLInputElement;
    const selectedStampVal = this.stampLocationControl?.value || '';
    const finalStampCode = this.stampLocationVisible ? selectedStampVal : '';
    this.externalStampLoc = finalStampCode;

    // Require stamp location selection when needed
    if (this.isStampLocationRequired && !this.externalStampLoc) {
      this.validationError = 'DOCUMENTS.SELECT_STAMP_LOCATION';
      input.value = "";
      return;
    }

    const file = input.files?.[0];
    if (!file) return;

    // Special case for template uploads
    if (this.documentServiceType == 'template') {
      const fileName = file.name.toLowerCase();
      const acceptedExtensions = this.getAcceptedExtensions();
      const fileExtension = `.${(fileName.split('.').pop() || '').toLowerCase()}`;
      const matchesAccept = acceptedExtensions.length
        ? acceptedExtensions.includes(fileExtension)
        : fileName.endsWith('.xlsx');

      if (!matchesAccept) {
        this.validationError = this.getTemplateUploadErrorKey(acceptedExtensions);
        input.value = '';
        return;
      } else {
        this.onUploadRemove.emit(evt);
        return;
      }
    }

    // Validate file type
    const error = this.validateFile(
      file,
      this.documentType,
      this.attachments,
      this.folder?.allowedDocumentTypes
    );
    if (error) {
      input.value = '';
      this.validationError = `DOCUMENTS.${error}`;
      return;
    }

    // SR check
    if (!this.folder?.folderId) {
      input.value = '';
      this.validationError = `DOCUMENTS.SR_NOT_CREATED`;
      this.folderMissing.emit();
      this.onUploadRemove.emit('c_sr');
      return;
    }



    this.sharedService
      .getEncryption(this.documentServiceType, { cooMot: this.mot, svsSubject: this.svsSubject, userType: this.documentUploadUserType })
      .subscribe({
        // This method is used to handle the next callback using encryption response
        next: (encryptionResponse: EncryptionResponse) => {
          if (!encryptionResponse) {
            this.validationError = 'ENCRYPTION_FAILED';
            input.value = '';
            return;
          }
          // Step 2: Decode response and stringify
          const response: any = Utils.decodeAndStringify(encryptionResponse);
          const decodedJson: string = JSON.stringify(response, null, 2);

          // Prepare meta with encryption value
          const meta = {
            documentTypeNameEn: this.documentType,
            folderId: this.folder.folderId,
            folderParentId: this.folder.folderParentId,
            folderParentType: this.folder.folderParentType,
            serviceType: environment.documentEncryption ? response.serviceType : this.documentServiceType,
            validation: environment.documentEncryption ? response.validation : this.validation,
            notes: this.notes ? this.notes : "",
            mot: environment.documentEncryption ? response.mot : this.mot ?? "",
            svsSubject: environment.documentEncryption ? response.svsSubject : this.svsSubject ?? "",
            userType: environment.documentEncryption ? response.userType : "Customer",
            externalStampLoc: finalStampCode,
          };

          // Create & show placeholder line
          const attachment: Attachment = {
            name: file.name,
            url: '',
            progress: 0,
            uploaded: false,
            externalStampLoc: '',
            notes: '',
            canDelete: false,
            fileFormat: this.extractFileFormat(file),
            fileSizeBytes: file.size
          };
          if (this.isMedCaseLayout) {
            this.attachments.unshift(attachment);
          } else {
            this.attachments.push(attachment);
          }

          // Disable choose button while uploading
          this.isUploading = true;
          input.disabled = true;
          let success = false;

          // Now proceed with upload
          this.attachmentService
            .uploadFile(ApiRoot.JUPITER_REST, ApiEndPoints.UPLOAD_DOCUMENT, file, meta)
            .pipe(
              finalize(() => {
                this.isUploading = false;
                input.disabled = false;
                this.cdr.detectChanges();
                if (!success) {
                  input.value = '';
                  this.cdr.detectChanges();
                }
              })
            )
            .subscribe({
              next: (e: any) => {
                attachment.progress = e.progress;
                this.cdr.detectChanges();
                if (e.response) {
                  success = true;
                  attachment.url = '';
                  attachment.uploaded = true;
                  attachment.documentType = this.documentType;
                  attachment.documentId = e.response.data.documentId;
                  attachment.serverResponse = e.response;
                  attachment.externalStampLoc = e.externalStampLoc;
                  attachment.notes = this.notes ? this.notes : "";
                  attachment.canDelete = e.response.data.documentId

                  if (!this.folder.documents) {
                    this.folder.documents = [];
                  }
                  if (this.isMedCaseLayout) {
                    this.folder.documents.unshift({
                      documentType: this.documentType,
                      documentId: attachment.documentId,
                      notes: attachment.notes,
                      canDelete: attachment.canDelete,
                      ...attachment.serverResponse.data,
                    });
                  } else {
                    this.folder.documents.push({
                      documentType: this.documentType,
                      documentId: attachment.documentId,
                      notes: attachment.notes,
                      canDelete: attachment.canDelete,
                      ...attachment.serverResponse.data,
                    });
                  }
                  this.validationError = "";
                  this.isDocumentsMissing = false;
                  this.sharedService.toastMessage('SUCCESS', 'DOCUMENT_UPLOADED', 'success');
                  input.value = "";
                  this.onUploadRemove.emit(attachment);
                }
              },
              error: (e) => {
                this.validationError = "";
                input.value = "";
                // this.validationError = e?.error?.error?.details[0];
                this.validationError = this.translateService.instant('DOCUMENTS.FAILED_TO_UPLOAD') //'FAILED_TO_UPLOAD';
                this.attachments = this.attachments.filter((a) => a !== attachment);
              }
            });
        },
       error: () => {
          this.validationError = this.translateService.instant('DOCUMENTS.ENCRYPTION_FAILED');
          input.value = '';
        }
      });
  }

  // This method is used to validate file using file, document type, attachments, and allowed document types
  validateFile(
    file: File,
    documentType: string,
    attachments: any[],
    allowedDocumentTypes: any[]
  ): string | null {
    const docConfig = allowedDocumentTypes.find(d => d.nameEn.toLowerCase() == documentType.toLowerCase());
    if (!docConfig) {
      return 'INVALID_DOCUMENT_TYPE';
    }
    let alreadyUploaded: any; //attachments.filter(att => att.documentType.toLowerCase() === documentType.toLowerCase());
    if (this.notes) {
      alreadyUploaded = attachments?.filter((doc: any) => doc.documentType?.toLowerCase() === documentType?.toLowerCase() && doc.notes?.toLowerCase() === this.notes?.toLowerCase());
    } else {
      alreadyUploaded = attachments?.filter((doc: any) => doc.documentType?.toLowerCase() === documentType?.toLowerCase() && (doc?.notes == null || doc?.notes == ""));
    }
    if (docConfig.canUploadMultiple === false && alreadyUploaded?.length > 0) {
      return 'NOT_ALLOWED_MULTI_UPLOAD';
    }

    const configuredExtensions = Array.isArray(docConfig.allowedExtensions)
      ? docConfig.allowedExtensions
        .map((extension: string) => extension.toLowerCase().replace(/^\./, ''))
        .filter(Boolean)
      : [];
    const fileExtension = file.name.includes('.')
      ? file.name.split('.').pop()?.toLowerCase() || ''
      : '';
    const isAllowed = configuredExtensions.length > 0
      ? configuredExtensions.includes(fileExtension)
      : file.type === 'application/pdf' || fileExtension === 'pdf';
    if (!isAllowed) {
      return 'INVALID_FILE_FORMAT';
    }
    // max size allowed 20MB
    const maxSizeBytes = 20 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return 'FILE_SIZE_LIMIT';
    }
    return null;
  }

  // This method is used to get documents by type using documents and document type
  getDocumentsByType(documents: any, documentType: string): any[] {
    if (!documents || !documents || !Array.isArray(documents)) {
      return [];
    }
    if (this.notes) {
      return documents
        .filter((doc: any) => doc.documentType === documentType && doc.notes?.toLowerCase() === this.notes?.toLowerCase())
        .map((doc: any) => ({
          ...doc,
          canDelete: doc?.ocrAutoUploaded ? false : doc?.canDelete
        }));
    } else {
      return documents
        .filter((doc: any) => doc.documentType === documentType && (doc.notes == null || doc.notes == ""))
        .map((doc: any) => ({
          ...doc,
          canDelete: doc?.ocrAutoUploaded ? false : doc?.canDelete
        }));
    }
  }

  // This method is used to handle remove event using att
  onRemove(att: Attachment): any {
    if (this.isRemoving) {
      return;
    }

    this.isRemoving = true;
    this.validationError = "";
    this.sharedService
      .getEncryption(this.documentServiceType, { cooMot: this.mot, svsSubject: this.svsSubject })
      .subscribe({
         next: (encryptionResponse: EncryptionResponse) => {
          if (!encryptionResponse.validation) {
            this.validationError = "ENCRYPTION_FAILED";
            this.isRemoving = false;
            return;
          }
          // Step 2: Decode response and stringify
          const response: any = Utils.decodeAndStringify(encryptionResponse);
          const decodedJson: string = JSON.stringify(response, null, 2);

          this.attachmentService.deleteRequest(ApiRoot.JUPITER_REST, ApiEndPoints.DELETE_DOCUMENT, {
              documentId: att.documentId,
              folderId: this.folder.folderId,
              folderParentId: this.folder.folderParentId,
              folderParentType: this.folder.folderParentType,
              validation: environment.documentEncryption ? response.validation : this.validation
            })
            .pipe(finalize(() => {
              this.isRemoving = false;
              this.cdr.detectChanges();
            }))
            .subscribe({
            next: (response: any) => {
              if (response && response.success == true) {
                const targetId = att?.documentId;
                if (targetId) {
                  this.attachments = (this.attachments || []).filter(
                    (a: any) => a?.documentId !== targetId
                  );
                } else {
                  this.attachments = (this.attachments || []).filter((a) => a !== att);
                }

                // Remove globally from folder.documents
                if (this.folder?.documents) {
                  this.folder.documents = this.folder.documents.filter(
                    (doc: any) => doc.documentId !== att.documentId
                  );
                }

                this.onUploadRemove.emit(this.attachments);
                this.cdr.detectChanges();
                this.sharedService.toastMessage('SUCCESS', 'DOCUMENT_REMOVED', 'success');
              }
            },
            error: (e) => {
              this.sharedService.toastMessage('ERROR', 'DOCUMENT_REMOVED_FAILED', 'error');
              //this.sharedService.toastMessage('ERROR', '', 'error', e?.error?.error?.details[0]);
            }
          });
        },
        error: () => {
          this.validationError = 'ENCRYPTION_FAILED';
          this.isRemoving = false;
          return;
        }
      })
  }

  // This method is used to download document using document id
  downloadDocument(attachment: any) {
    const folderId = attachment?.memberFolderId ? attachment?.memberFolderId : this.folder.folderId;
    this.downloadingId = attachment.documentId;
    this.sharedService
      .getEncryption(this.documentServiceType, { cooMot: this.mot, svsSubject: this.svsSubject })
      .subscribe({
        next: (encryptionResponse: EncryptionResponse) => {
          if (!encryptionResponse.validation) {
            this.validationError = "ENCRYPTION_FAILED";
            return;
          }
          // Step 2: Decode response and stringify
          const response: any = Utils.decodeAndStringify(encryptionResponse);
          const decodedJson: string = JSON.stringify(response, null, 2);

          let validation = environment.documentEncryption ? response.validation : this.validation;
          this.attachmentService
            .downloadFile(this.downloadingId, folderId, validation)
            .pipe(finalize(() => (this.downloadingId = null)))
            .subscribe({
              // This method is used to handle the next callback using blob
              next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // Try to use the attachment's name/label; fallback to generic name
                const att = (this.attachments || []).find((x: any) => x.documentId === this.downloadingId);
                const fileName = att?.name || att?.label || 'downloaded-document';
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
                this.downloadingId = null;
                this.cdr.detectChanges()
              },
              error: (e) => {
                this.sharedService.toastMessage('ERROR', '', 'error', e?.error?.error?.details[0]);
              },
            });
        }
      })
  }

  /** Return true if this document is mandatory but missing */
  isDocumentsMissing: boolean = false;
  checkMissing(mandatory: boolean): boolean {
    this.isDocumentsMissing = false;
    if (!mandatory) return false;

    let documents: any = this.folder?.documents;
    let uploaded: any;
    if (this.notes) {
      uploaded = documents?.some((doc: any) => doc.documentType === this.documentType && doc.notes?.toLowerCase() === this.notes?.toLowerCase());
    } else {
      uploaded = documents?.some((doc: any) => doc.documentType === this.documentType && (doc.notes == null || doc.notes == ""));
    }
    this.isDocumentsMissing = !uploaded;
    return this.isDocumentsMissing;
  }

  // This method is used to get attachment stamp code using att
  getAttachmentStampCode(att: any): string | null {
    return (
      att?.externalStampLoc ||
      att?.serverResponse?.data?.externalStampLoc ||
      att?.serverResponse?.externalStampLoc ||
      null
    );
  }

  // This method is used to get stamp short text using code
  getStampShortText(code: string | null | undefined): string {
    const c = (code || '').toUpperCase();
    let stamp: any = this.stampPositions.find((s: any) => s.value.toUpperCase() == c);
    return stamp?.label;
  }

  private removeAttachmentsForStampChange(): void {
    if (this.isRemoving) {
      return;
    }

    const currentAttachments = [...(this.attachments || [])];
    const persistedAttachments = currentAttachments.filter((att) => !!att?.documentId);
    let localRemoval = false;

    for (const att of currentAttachments) {
      if (!att?.documentId) {
        this.attachments = this.attachments.filter((a) => a !== att);
        localRemoval = true;
      }
    }

    if (persistedAttachments.length) {
      this.bulkRemoveDocuments(persistedAttachments);
    }

    if (localRemoval) {
      this.onUploadRemove.emit(this.attachments);
    }
  }

  private get normalizedStampRequirement(): StampLocationRequirement {
    if (this.stampLocationRequirement === 'REQUIRED' || this.stampLocationRequirement === 'OPTIONAL') {
      return this.stampLocationRequirement;
    }
    return 'NOT_APPLICABLE';
  }

  get stampLocationVisible(): boolean {
    return this.normalizedStampRequirement !== 'NOT_APPLICABLE';
  }

  get isStampLocationRequired(): boolean {
    return this.normalizedStampRequirement === 'REQUIRED';
  }

  get isMedCaseLayout(): boolean {
    return this.documentServiceType === 'MED_CASE';
  }

  getProgressPercent(attachment: any): number {
    if (attachment?.uploaded || attachment?.documentId) {
      return 100;
    }
    const progress = Number(attachment?.progress ?? 0);
    if (Number.isNaN(progress)) {
      return 0;
    }
    return Math.max(0, Math.min(100, progress));
  }

  getAttachmentFormat(att: any): string {
    if (att?.fileFormat) {
      return att.fileFormat;
    }
    const fileName = att?.name || att?.label || '';
    const ext = fileName.includes('.') ? fileName.split('.').pop() : '';
    return ext ? ext.toUpperCase() : '';
  }

  getAttachmentSize(att: any): string {
    const bytes = Number(att?.fileSizeBytes ?? att?.size ?? att?.fileSize ?? 0);
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '';
    }
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
      return `${Math.round(mb)}MB`;
    }
    const kb = bytes / 1024;
    return `${Math.max(1, Math.round(kb))}KB`;
  }

  private extractFileFormat(file: File): string {
    const ext = file?.name?.includes('.') ? file.name.split('.').pop() : '';
    if (ext) {
      return ext.toUpperCase();
    }
    const mime = file?.type || '';
    if (mime.includes('/')) {
      return mime.split('/')[1].toUpperCase();
    }
    return '';
  }

  private getAcceptedExtensions(): string[] {
    const acceptValue = (this.accept || '').toString().trim().toLowerCase();
    if (!acceptValue) {
      return [];
    }
    return acceptValue
      .split(',')
      .map((item: string) => item.trim())
      .filter((item: string) => item.startsWith('.'));
  }

  private getTemplateUploadErrorKey(extensions: string[]): string {
    if (extensions.includes('.json')) {
      return 'DOCUMENTS.UPLOAD_JSON';
    }
    return 'DOCUMENTS.UPLOAD_XLSX';
  }

  get templateFormatLabel(): string {
    const exts = this.getAcceptedExtensions();
    if (exts.length) {
      return exts.map((ext) => ext.replace('.', '').toUpperCase()).join(', ');
    }
    return 'XLSX';
  }

  get allowedFormatLabel(): string {
    const documentConfig = this.folder?.allowedDocumentTypes
      ?.find((type: any) => type.nameEn?.toLowerCase() === this.documentType?.toLowerCase());
    const extensions = Array.isArray(documentConfig?.allowedExtensions)
      ? documentConfig.allowedExtensions.filter(Boolean)
      : [];
    return (extensions.length ? extensions : ['pdf'])
      .map((extension: string) => extension.replace(/^\./, '').toUpperCase())
      .join(', ');
  }

  get medCaseDisplayAttachments(): any[] {
    const currentType = (this.documentType || '').toLowerCase();
    const currentTitle = this.resolveDocumentTypeTitle(this.documentType);
    const currentAttachments = (this.attachments || []).map((att: any) => ({
      ...att,
      _docTitle: currentTitle
    }));

    if (!this.isMedCaseLayout || !this.allowedDocumentTypes?.length) {
      return currentAttachments;
    }

    const allowedTypes = new Set(
      this.allowedDocumentTypes.map((dt: any) => (dt?.nameEn || '').toLowerCase())
    );

    const folderDocs = (this.folder?.documents || [])
      .filter((doc: any) => {
        const docType = (doc?.documentType || '').toLowerCase();
        return !!doc?.documentId && allowedTypes.has(docType) && docType !== currentType;
      })
      .map((doc: any) => ({
        ...doc,
        name: doc.name || doc.label || doc.fileName || '',
        uploaded: true,
        _docTitle: this.resolveDocumentTypeTitle(doc.documentType)
      }));

    const systemDocs = (this.systemDocuments || [])
      .map((item: any) => item?.doc)
      .filter((doc: any) => !!doc?.documentId)
      .map((doc: any) => ({
        ...doc,
        name: doc.name || doc.label || doc.fileName || '',
        uploaded: true,
        canDelete: false,
        _docTitle: doc.label || doc.documentType || this.title
      }));

    return [...currentAttachments, ...folderDocs, ...systemDocs];
  }

  private resolveDocumentTypeTitle(type: string): string {
    if (!type) {
      return this.title || '';
    }
    const match = (this.allowedDocumentTypes || []).find(
      (dt: any) => (dt?.nameEn || '').toLowerCase() === type.toLowerCase()
    );
    if (!match) {
      return this.title || type;
    }
    return this.lang === 'en' ? match.nameEn : (match.nameAr || match.nameEn);
  }

  private removeDeletedDocumentsFromLocalState(deletedDocumentIds: string[]): void {
    if (!deletedDocumentIds.length) {
      return;
    }

    this.attachments = (this.attachments || []).filter(
      (attachment: any) => !deletedDocumentIds.includes(attachment?.documentId)
    );

    if (this.folder?.documents) {
      this.folder.documents = this.folder.documents.filter(
        (doc: any) => !deletedDocumentIds.includes(doc?.documentId)
      );
    }
  }

  // This method is used to handle bulk remove event using attachments
  bulkRemoveDocuments(attachments: Attachment[]): any {
    if (this.isRemoving) {
      return;
    }

    this.validationError = "";
    const documentIds = (attachments || [])
      .map((attachment: Attachment) => attachment?.documentId)
      .filter((documentId: any) => !!documentId);

    if (!documentIds.length) {
      return;
    }

    this.isRemoving = true;
    this.sharedService
      .getEncryption(this.documentServiceType, { cooMot: this.mot, svsSubject: this.svsSubject })
      .subscribe({
        next: (encryptionResponse: EncryptionResponse) => {
          if (!encryptionResponse.validation) {
            this.validationError = "ENCRYPTION_FAILED";
            this.isRemoving = false;
            return;
          }
          // Step 2: Decode response and stringify
          const response: any = Utils.decodeAndStringify(encryptionResponse);
          const decodedJson: string = JSON.stringify(response, null, 2);

          this.attachmentService.deleteBulkDocuments(ApiRoot.JUPITER_REST, ApiEndPoints.DELETE_BULK_DOCUMENT, {
              documentIds,
              folderId: this.folder.folderId,
              folderParentId: this.folder.folderParentId,
              folderParentType: this.folder.folderParentType,
              validation: environment.documentEncryption ? response.validation : this.validation
            })
            .pipe(finalize(() => {
              this.isRemoving = false;
              this.cdr.detectChanges();
            }))
            .subscribe({
            next: (response: any) => {
              if (response && response.success == true) {
                const deletedDocumentIds = response?.data?.deletedDocuments || [];
                const failedDeletes = Number(response?.data?.failedDeletes || 0);
                const successfulDeletes = Number(response?.data?.successfulDeletes || 0);

                this.removeDeletedDocumentsFromLocalState(deletedDocumentIds);

                this.onUploadRemove.emit(this.attachments);
                this.cdr.detectChanges();

                if (failedDeletes > 0) {
                   this.sharedService.toastMessage('ERROR', 'DOCUMENT_REMOVED_FAILED', 'error');
                  return;
                }
              }
            },
            error: (e) => {
              this.sharedService.toastMessage('ERROR', 'DOCUMENT_REMOVED_FAILED', 'error');
            }
          });
        },
        error: () => {
          this.validationError = 'ENCRYPTION_FAILED';
          this.isRemoving = false;
          return;
        }
      })
  }

}
